import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { PageHeader } from '@opengov/components-page-header';
import { Download, Magnify } from '@opengov/react-capital-assets';

import { CaseStatusBadge } from '../components/vcm/CaseStatusBadge';
import { SectionCard } from '../components/vcm/SectionCard';
import { TierBadge } from '../components/vcm/TierBadge';
import { useAppNavigation } from '../hooks/useAppNavigation';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import { mockGetCases } from '../mocks/vcm-mock-data';

export interface CaseListPageProps {
  readonly title: string;
  readonly description: string;
  /** Shows an Export action in the header. */
  readonly showExport?: boolean;
}

export function CaseListPage({ title, description, showExport = false }: CaseListPageProps) {
  const { navigateToPath } = useAppNavigation();
  const [query, setQuery] = useState('');

  /* MOCK DATA */
  const cases = useMemo(() => mockGetCases(), []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return cases;
    return cases.filter((c) =>
      `${c.caseId} ${c.clientName} ${c.organization}`.toLowerCase().includes(q),
    );
  }, [cases, query]);

  return (
    <Box
      sx={{
        p: (theme) => theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(2.5),
      }}
    >
      <PageHeader
        title={title}
        description={description}
        {...(showExport
          ? {
              actions: [
                <Button key="export" variant="outlined" startIcon={<Download />}>
                  Export
                </Button>,
              ],
            }
          : {})}
      />

      <SectionCard title={`Cases (${filtered.length})`} disableContentPadding>
        <Box sx={{ p: (theme) => theme.spacing(2) }}>
          <TextField
            placeholder="Search by case ID, client, or organization…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="small"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <Box
                    sx={{
                      display: 'flex',
                      mr: 1,
                      color: (t) => t.palette.text.secondary,
                      '& svg': { width: 18, height: 18 },
                    }}
                  >
                    <Magnify />
                  </Box>
                ),
              },
            }}
          />
        </Box>
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center">Case ID</TableCell>
                <TableCell align="center">Client</TableCell>
                <TableCell align="center">Organization</TableCell>
                <TableCell align="center" sx={{ width: 100 }}>Progression</TableCell>
                <TableCell align="center" sx={{ width: 80 }}>Tier</TableCell>
                <TableCell align="center" sx={{ width: 90 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((tcrCase) => (
                <TableRow
                  key={tcrCase.caseId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigateToPath(`cases/${tcrCase.caseId}`)}
                >
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {tcrCase.caseId}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{tcrCase.clientName}</TableCell>
                  <TableCell align="center">{tcrCase.organization}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{tcrCase.progression}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <TierBadge tier={tcrCase.tier} />
                  </TableCell>
                  <TableCell align="center">
                    <CaseStatusBadge status={tcrCase.status} />
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: (theme) => theme.spacing(4) }}>
                    <Typography
                      variant="body2"
                      sx={{ color: (theme) => theme.palette.text.secondary }}
                    >
                      No cases match your search.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>
    </Box>
  );
}
